import { createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"

import { IOneVerifyOrganizationUserInvite } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"

import { LoginStep } from "../../config/auth.config"
import { GENERAL_CONFIG } from "../../config/general.config"
import type { ILoginData, ITOTPSetupDetails } from "../../types/data.types"
import type { IAuthState } from "../../types/state.types"

const initialState: IAuthState = {
    isLoading: false,
    loginStep: LoginStep.EMAIL,
    loginEmail: null,
    loginData: null,
    totpSetupDetails: null,
    autoSubmitLoginPasswordStep: false,
    mfaChallengePhoneNumber: null,
    forgotPasswordEmail: undefined,
    userInvitation: undefined,
}

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        authReset: () => {
            return initialState
        },
        authResetAuthFlowState: state => {
            return {
                ...initialState,
                userInvitation: state.userInvitation,
            }
        },
        authSetIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },
        authSetLoginStep: (state, action: PayloadAction<LoginStep>) => {
            state.loginStep = action.payload
        },
        authSetLoginEmail: (state, action: PayloadAction<string | null>) => {
            state.loginEmail = action.payload
        },
        authSetLoginData: (state, action: PayloadAction<ILoginData | null>) => {
            state.loginData = action.payload
        },
        authSetTotpSetupDetails: (
            state,
            action: PayloadAction<ITOTPSetupDetails | null>,
        ) => {
            state.totpSetupDetails = action.payload
        },
        authSetAutoSubmitLoginPasswordStep: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.autoSubmitLoginPasswordStep = action.payload
        },
        authSetMfaChallengePhoneNumber: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.mfaChallengePhoneNumber = action.payload
        },
        authSetForgotPasswordEmail: (
            state,
            action: PayloadAction<{ email: string }>,
        ) => {
            state.forgotPasswordEmail = action.payload.email
        },
        authSetCheckUserInvitation: (
            state,
            action: PayloadAction<IOneVerifyOrganizationUserInvite | undefined>,
        ) => {
            if (!action.payload) {
                BrowserStorageHelper.remove(
                    GENERAL_CONFIG.browserStorageKeys.inviteToken,
                    BrowserStorageType.sessionStorage,
                )
            }

            state.userInvitation = action.payload
        },
    },
})

export const {
    authReset,
    authResetAuthFlowState,
    authSetIsLoading,
    authSetLoginStep,
    authSetLoginEmail,
    authSetLoginData,
    authSetTotpSetupDetails,
    authSetAutoSubmitLoginPasswordStep,
    authSetMfaChallengePhoneNumber,
    authSetForgotPasswordEmail,
    authSetCheckUserInvitation,
} = authSlice.actions

export const authReducer = authSlice.reducer
